import { useEffect, useMemo, useState } from "react";
import { FiEdit2, FiEye, FiSearch, FiTrash2 } from "react-icons/fi";
import DashboardLayout from "../../components/DashboardLayout";
import LoaderComp from "../../components/LoaderComp";
import RenderToast from "../../components/RenderToast";
import useDebounce from "../../hooks/useDebounce";
import DeleteUserModal from "../../Modal/User/DeleteUserModal";
import EditUserModal from "../../Modal/User/EditUserModal";
import ViewUserModal from "../../Modal/User/ViewUserModal";
import API from "../../utils/api";
import classes from "./Users.module.css";

const readUser = () => {
  try {
    const raw = localStorage.getItem("user");
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

export default function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [viewTarget, setViewTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const debouncedSearch = useDebounce(search, 400);
  const [currentUser, setCurrentUser] = useState(readUser());

  useEffect(() => {
    const handleStorage = () => setCurrentUser(readUser());
    const handleUserUpdate = () => setCurrentUser(readUser());
    window.addEventListener("storage", handleStorage);
    window.addEventListener("userUpdated", handleUserUpdate);
    return () => {
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("userUpdated", handleUserUpdate);
    };
  }, []);

  const can = (permission) =>
    currentUser && Number(currentUser[permission]) === 1;
  const isAdmin = currentUser?.role === "admin";
  // Admins can always access user management page
  const canViewUser = can("user_manage") || isAdmin;
  // Admins can always edit/view/delete users, or users with specific permissions
  const canEditUser = isAdmin || can("user_edit_action");
  const canViewUserAction = isAdmin || can("user_view_action");
  const canDeleteUser = isAdmin || can("user_delete_action");

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken") || "";
      const response = await fetch(`${API.base}/users.php`, {
        method: "POST",
        headers: {
          Authorization: token,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams({ action: "fetch" }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Failed to load users");
      setUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      RenderToast({ type: "error", message: err.message });
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const filteredUsers = useMemo(() => {
    if (!debouncedSearch.trim()) return users;
    const term = debouncedSearch.toLowerCase();
    return users.filter(
      (user) =>
        user.name.toLowerCase().includes(term) ||
        user.email.toLowerCase().includes(term) ||
        (user.phone || "").toLowerCase().includes(term)
    );
  }, [users, debouncedSearch]);

  const handleDelete = async () => {
    if (!canDeleteUser || !deleteTarget) return;
    setDeleting(true);
    try {
      const token = localStorage.getItem("authToken") || "";
      const params = new URLSearchParams();
      params.append("action", "delete");
      params.append("id", deleteTarget.id);
      const response = await fetch(`${API.base}/users.php`, {
        method: "POST",
        headers: {
          Authorization: token,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: params,
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || "Failed to delete user");
      }
      RenderToast({ type: "success", message: "User deleted successfully" });
      setDeleteTarget(null);
      fetchUsers();
    } catch (err) {
      RenderToast({ type: "error", message: err.message });
    } finally {
      setDeleting(false);
    }
  };

  if (!canViewUser) {
    return (
      <DashboardLayout>
        <div className={classes.page}>
          <div className={classes.empty}>
            You do not have permission to view users.
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className={classes.page}>
        {/* Top Bar */}
        <div className={classes.header}>
          <div>
            <p className={classes.kicker}>Team</p>
            <h2 className={classes.title}>User Access</h2>
            <p className={classes.subtitle}>
              Review account details and control access to admin tools.
            </p>
          </div>
          <div className={classes.searchWrap}>
            <FiSearch />
            <input
              type="search"
              placeholder="Search by name, email, phone..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {/* User Table Card */}
        <div className={classes.card}>
          {loading ? (
            <LoaderComp />
          ) : filteredUsers.length === 0 ? (
            <div className={classes.empty}>No users found.</div>
          ) : (
            <div className={classes.tableWrapper}>
              <table>
                <thead>
                  <tr>
                    <th>S.No</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Role</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user, idx) => (
                    <tr key={user.id}>
                      <td>{idx + 1}</td>
                      <td>{user.name || "—"}</td>
                      <td>{user.email}</td>
                      <td>{user.phone || "—"}</td>
                      <td className={classes.roleCell}>
                        {user.role === "admin" ? "Administrator" : "User"}
                      </td>
                      <td>
                        <div className={classes.actions}>
                          {canViewUserAction && (
                            <button
                              type="button"
                              onClick={() => setViewTarget(user)}
                              title="View"
                            >
                              <FiEye />
                            </button>
                          )}
                          {canEditUser && (
                            <button
                              type="button"
                              onClick={() => setEditTarget(user)}
                              title="Edit"
                            >
                              <FiEdit2 />
                            </button>
                          )}
                          {canDeleteUser && (
                            <button
                              type="button"
                              onClick={() => setDeleteTarget(user)}
                              title="Delete"
                              className={classes.deleteBtn}
                            >
                              <FiTrash2 />
                            </button>
                          )}
                          {!canViewUserAction &&
                            !canEditUser &&
                            !canDeleteUser && (
                              <span className={classes.noActions}>—</span>
                            )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {viewTarget && canViewUserAction && (
        <ViewUserModal user={viewTarget} onClose={() => setViewTarget(null)} />
      )}
      {editTarget && canEditUser && (
        <EditUserModal
          user={editTarget}
          onClose={() => setEditTarget(null)}
          onUpdated={fetchUsers}
        />
      )}
      {deleteTarget && canDeleteUser && (
        <DeleteUserModal
          user={deleteTarget}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={handleDelete}
          loading={deleting}
        />
      )}
    </DashboardLayout>
  );
}
